import { createContext, useContext, useState, useEffect } from 'react';
import { API } from '@/config/api';
import { trackLogin, trackSignUp } from '@/components/SEO';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      // Check if we have a token in localStorage
      const token = localStorage.getItem('auth_token');
      
      if (!token) {
        setLoading(false);
        return;
      }
      
      const response = await fetch(`${API}/auth/me`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const userData = await response.json();
        // Create a clean user object
        setUser({
          user_id: userData.user_id,
          email: userData.email,
          name: userData.name,
          picture: userData.picture || null,
          membership: userData.membership || 'free',
          membership_tier: userData.membership_tier || 'legacy_explorer',
          has_trading_access: userData.has_trading_access || false
        });
        setIsAuthenticated(true);
      } else {
        // Clear invalid token
        localStorage.removeItem('auth_token');
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('auth_token');
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  // Login with email and password (supports 2FA)
  const login = async (email, password, twoFactorCode = null) => {
    try {
      const endpoint = twoFactorCode ? `${API}/auth/login-2fa` : `${API}/auth/login`;
      const body = twoFactorCode 
        ? { email, password, code: twoFactorCode }
        : { email, password };
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        return { success: false, error: errorData.detail || 'Login failed' };
      }
      
      const data = await response.json();
      
      // Check if 2FA is required
      if (data.requires_2fa) {
        return { success: false, requires_2fa: true };
      }
      
      // Store token
      if (data.token) {
        localStorage.setItem('auth_token', data.token);
      }
      
      // Create a clean user object to avoid cloning issues
      const cleanUser = {
        user_id: data.user_id,
        email: data.email,
        name: data.name,
        picture: data.picture || null,
        membership: data.membership || 'free',
        membership_tier: data.membership_tier || 'legacy_explorer',
        two_factor_enabled: data.two_factor_enabled || false
      };
      
      setUser(cleanUser);
      setIsAuthenticated(true);
      
      // Track login event for analytics
      trackLogin();
      
      return { success: true };
    } catch (error) {
      console.error('Login failed:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  // Register new user
  const register = async (email, password, name) => {
    try {
      const response = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email, password, name }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        return { success: false, error: errorData.detail || 'Registration failed' };
      }
      
      const data = await response.json();
      
      // Store token
      if (data.token) {
        localStorage.setItem('auth_token', data.token);
      }
      
      // Create a clean user object
      const cleanUser = {
        user_id: data.user_id,
        email: data.email,
        name: data.name,
        picture: null,
        membership: data.membership || 'free',
        membership_tier: data.membership_tier || 'legacy_explorer'
      };
      
      setUser(cleanUser);
      setIsAuthenticated(true);
      
      // Track sign up event for analytics
      trackSignUp();
      
      return { success: true };
    } catch (error) {
      console.error('Registration failed:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  // Logout function
  const logout = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('auth_token');
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    register,
    logout,
    checkAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
